// Fuentes separadas por alcance: NO fusionar alérgenos antiguos con los nuevos.
//   - carta-restaurante-2026-10-09.json: impreso actualizado + variantes del nuevo PDF.
//   - allergens-2026-10-09.json: única autoridad de alérgenos del restaurante.
//   - carta-infantil-2026.json / alergenos-2026-v2.json: infantil anterior, sin cambios.
//   - I18N (abajo)                                             -> localización EN/VA (VA = PROPUESTA, revisión humana antes de publicar)
// i18n.js IMPORTA lo que exporta este módulo. No se hardcodean platos/precios/alérgenos en componentes.
import restauranteSrc from "@/data/carta-restaurante-2026-10-09.json";
import infantilSrc from "@/data/carta-infantil-2026.json";
import alergenosSrc from "@/data/alergenos-2026-v2.json";
import { CURRENT_ALLERGEN_INDEX, MENU_NOTICES, getAllergenState } from "@/data/menuAllergenContent";

// ── Localización EN/VA (overlay por id de plato). ES viene del JSON fuente. Si falta un idioma,
// `t()` cae a ES. VA es PROPUESTA pendiente de visto bueno humano antes de desplegar. ────────────
const I18N = {
  // Para compartir
  "bravas-chipotle": { en: "Bravas with smoked chipotle sauce", va: "Braves amb salsa de chipotle fumat" },
  "patatas-ritual": { en: "Ritual Fries", va: "Creïlles Ritual", d_en: "With carbonara sauce, guanciale and pecorino romano", d_va: "Amb salsa carbonara, guanciale i pecorino romà" },
  "bao-pollo-limeno": { en: "Limeño chicken bao", va: "Bao de pollastre limeny", d_en: "Bao filled with citrus potato purée and chicken in mayonnaise", d_va: "Bao farcit de puré de creïlla cítric i pollastre en maionesa" },
  "bao-costilla-asada": { en: "Roast rib bao", va: "Bao de costella rostida", d_en: "With coleslaw, barbecue sauce and corn nuts", d_va: "Amb coleslaw, salsa barbacoa i quicos" },
  "nachos-maiz": { en: "Corn nachos", va: "Nachos de dacsa", d_en: "With bolognese, guacamole, cheddar, sour cream and pico de gallo", d_va: "Amb bolonyesa, guacamole, cheddar, crema agra i pico de gallo" },
  "calamares": { en: "Squid", va: "Calamars", d_en: "Andalusian-style with black aioli and lemon zest", d_va: "A l'andalusa amb allioli negre i ratlladura de llimó" },
  "huevos-rotos-jamon": { en: "Broken eggs with ham", va: "Ous trencats amb pernil" },
  "huevos-rotos-trufados": { en: "Truffled broken eggs", va: "Ous trencats tofonats", d_en: "With sautéed boletus", d_va: "Amb bolets saltejats" },
  "sepia": { en: "Cuttlefish with mery sauce and black aioli", va: "Sépia amb salsa mery i allioli negre", d_en: "Grilled", d_va: "A la planxa" },
  "lagrimas-pollo": { en: "Chicken tears", va: "Llàgrimes de pollastre", d_en: "With smoked chipotle sauce and citrus zest", d_va: "Amb salsa chipotle fumat i ratlladura cítrica" },
  "berenjenas-miel": { en: "Aubergines with honey", va: "Albergínies amb mel" },
  "croqueta-chuleta": { en: "Aged beef chop croquette", va: "Croqueta de costella madurada" },
  "croqueta-jamon": { en: "Ham croquette", va: "Croqueta de pernil" },
  "ensalada-tomate": { en: "Valencian tomato salad", va: "Amanida de tomaca valenciana", d_en: "With our tomato and sherry vinaigrette and fresh basil. Add tuna belly for €2.50", d_va: "Amb la nostra vinagreta de tomaca i Xerès, i alfàbega fresca. Afig ventresca de tonyina per 2,50€" },
  "ensalada-pollo-crujiente": { en: "Crispy chicken salad", va: "Amanida de pollastre cruixent", d_en: "Salad sprouts, peppers, avocado, tomato, crispy chicken and honey-mustard sauce", d_va: "Brots d'amanida, pebres, alvocat, tomaca, pollastre cruixent i salsa mostassa i mel" },
  "causa-limena": { en: "Limeña causa", va: "Causa limenya", d_en: "Avocado, potato, onion, chicken and lime", d_va: "Alvocat, creïlla, ceba, pollastre i llima" },
  "burrata": { en: "Burrata (Winner of the World Championship Cheese Contest 2025)", va: "Burrata (Guanyadora del concurs World Championship Cheese Contest 2025)", d_en: "Served with a reduced tomato, onion and garlic sauce, roasted cherry tomatoes, pesto and rocket", d_va: "Servida amb salsa reduïda de tomaca, ceba i all, cherrys rostits, pesto i ruca" },
  // Principales
  "ravioli": { en: "Ravioli", va: "Ravioli", d_en: "Beef with truffle cream", d_va: "De vedella amb crema de tòfona" },
  "sorrentino": { en: "Roasted vegetable sorrentino", va: "Sorrentino de verdures rostides", d_en: "With Napoli sauce and roasted cherry tomatoes", d_va: "Amb salsa Nàpols i tomaques cherry rostides" },
  "rigatoni-ragu": { en: "Rigatoni al ragù", va: "Rigatoni al ragú", d_en: "8-hour stew of carrot, celery, onion and minced meat", d_va: "Estofat de 8 hores a partir de safanòria, api, ceba i carn picada" },
  "rigatoni-carbonara": { en: "Rigatoni carbonara", va: "Rigatoni a la carbonara", d_en: "With pecorino and guanciale", d_va: "Amb pecorino i guanciale" },
  "burger-ritual": { en: "Ritual Burger", va: "Burger Ritual", d_en: "With guanciale, cheddar, onion, pickle, tomato, lettuce and mustard", d_va: "Amb guanciale, cheddar, ceba, cogombret, tomaca, lletuga i mostassa" },
  "burger-clasica": { en: "Classic Burger", va: "Burger Clàssica", d_en: "With bacon, cheddar, tomato, lettuce and barbecue sauce", d_va: "Amb bacó, cheddar, tomaca, lletuga i salsa barbacoa" },
  "burger-pollo-crujiente": { en: "Crispy chicken burger", va: "Burger de pollastre cruixent", d_en: "With coleslaw and pickles", d_va: "Amb coleslaw i cogombrets" },
  "burger-trufada": { en: "Truffle burger", va: "Burger tofonada", d_en: "With brie cheese, truffle mayo, tomato and rocket", d_va: "Amb formatge brie, maionesa de tòfona, tomaca i ruca" },
  "entrecot": { en: "Sirloin steak", va: "Entrecot", d_en: "With potatoes and confit piquillo peppers", d_va: "Amb creïlles i pebres de piquillo confitats" },
  "carrillera": { en: "Beef cheek", va: "Galta de vedella", d_en: "With parmentier and confit piquillo peppers", d_va: "Amb parmentier i pebres de piquillo confitats" },
  "costillas-asadas": { en: "Roast ribs", va: "Costelles rostides", d_en: "Cooked at low temperature for 10 hours and roasted with barbecue sauce. Served with potatoes", d_va: "Cuinades a baixa temperatura durant 10 hores, i rostides amb salsa barbacoa. Acompanyada de creïlles" },
  "lubina": { en: "Sea bass", va: "Llobarro", d_en: "With roasted seasonal vegetables", d_va: "Amb verdures de temporada rostides" },
  "salmon": { en: "Salmon", va: "Salmó", d_en: "With parmentier and roasted seasonal vegetables", d_va: "Amb parmentier i verdures de temporada rostides" },
  // Un cachito de tarta
  "tarta-queso-nutella": { en: "Classic cheesecake with Nutella", va: "Pastís de formatge clàssic amb Nutella" },
  "tarta-queso-lotus": { en: "Lotus cheesecake", va: "Pastís de formatge Lotus" },
  "tarta-chocolate": { en: "Chocolate cake", va: "Pastís de xocolata", d_en: "For chocolate lovers", d_va: "Per als xocolaters" },
  // Infantil
  "inf-hamburguesa-queso": { en: "Cheeseburger", va: "Hamburguesa amb formatge" },
  "inf-calamares": { en: "Battered squid", va: "Calamars arrebossats", d_en: "With fries", d_va: "Amb creïlles" },
  "inf-lagrimas-pollo": { en: "Chicken tears", va: "Llàgrimes de pollastre", d_en: "With fries", d_va: "Amb creïlles" },
  "inf-macarrones-queso": { en: "Mac and cheese", va: "Macarrons amb formatge" },
  "inf-macarrones-bolonesa": { en: "Bolognese macaroni", va: "Macarrons a la bolonyesa" },
  "inf-espagueti-carbonara": { en: "Spaghetti carbonara", va: "Espagueti carbonara" },
  "inf-macarrones-tomate": { en: "Macaroni with tomato", va: "Macarrons amb tomaca" },
};

// Section titles (trilingüe). ES viene del JSON `nombre.es`.
const SECTION_I18N = {
  "compartir": { en: "To Share", va: "Per a Compartir" },
  "ensaladas": { en: "Salads", va: "Amanides" },
  "principales": { en: "Mains", va: "Principals" },
  "tartas": { en: "A slice of cake", va: "Un trosset de pastís" },
  "infantil-platos": { en: "Kids' dishes", va: "Plats infantils" },
};

// Tab labels
export const RESTAURANTE_LABEL = { es: "Carta Restaurante", en: "Restaurant Menu", va: "Carta Restaurant" };
export const INFANTIL_LABEL = { es: "Carta infantil", en: "Kids' Menu", va: "Carta infantil" };

// Índice de infantil previo y restaurante nuevo; ninguna fila antigua de restaurante se mezcla.
const allergenIndex = {};
for (const sec of alergenosSrc.secciones) {
  if (sec.carta === "infantil") {
    for (const p of sec.platos) {
      allergenIndex[p.id] = { a: Array.isArray(p.alergenos) ? p.alergenos : [], revisado: p.revisado === true };
    }
  }
}
for (const [id, record] of Object.entries(CURRENT_ALLERGEN_INDEX)) {
  allergenIndex[id] = { a: record.confirmed, revisado: getAllergenState(record) !== "pending", allergenRecord: record };
}
export const ALLERGEN_INDEX = allergenIndex;

// nota_general trilingüe (ES = autoridad del JSON).
export const NOTA_GENERAL = {
  es: alergenosSrc.nota_general,
  en: "Prepared in a kitchen where all allergens are handled. Please ask our floor staff about any allergy or intolerance.",
  va: "Elaborat en una cuina on es manipulen tots els al·lèrgens. Consulta el personal de sala davant qualsevol al·lèrgia o intolerància.",
};

const eur = (v) => `${Number(v).toFixed(2).replace(".", ",")}€`;

function buildItem(it) {
  const tr = I18N[it.id] || {};
  const alg = allergenIndex[it.id] || { a: [], revisado: false };
  const dEs = it.d || "";
  return {
    id: it.id,
    n: { es: it.n, en: tr.en || it.n, va: tr.va || it.n },
    d: dEs ? { es: dEs, en: tr.d_en || dEs, va: tr.d_va || dEs } : { es: "", en: "", va: "" },
    p: (it.precio || it.precio === 0) ? eur(it.precio) : "",
    a: alg.a,
    revisado: alg.revisado, // marca los items sujetos a las reglas de alérgenos del Bloque A
    allergenRecord: alg.allergenRecord,
    priceStatus: it.priceStatus,
  };
}

function buildSections(src) {
  return src.secciones.map((sec) => ({
    id: sec.id,
    title: { es: sec.nombre.es, en: (SECTION_I18N[sec.id] || {}).en || sec.nombre.es, va: (SECTION_I18N[sec.id] || {}).va || sec.nombre.es },
    items: sec.items.map(buildItem),
  }));
}

export const RESTAURANTE_TAB = {
  key: "restaurante",
  label: RESTAURANTE_LABEL,
  notaGeneral: MENU_NOTICES.short,
  sections: buildSections(restauranteSrc),
};

export const INFANTIL_TAB = {
  key: "infantil",
  label: INFANTIL_LABEL,
  notaGeneral: NOTA_GENERAL,
  infantil: true, // estilo de tarjetas más alegre en la página
  sections: buildSections(infantilSrc),
};

// Validación de claves de alérgeno REALMENTE USADAS (restaurante + infantil) contra el mapa
// c.allergens. Devuelve la lista de claves que no resuelven. Se usa por el gate de build
// (scripts/validate-carta.js) y como guarda de runtime.
export function findUnresolvedAllergenKeys(allergenMap) {
  const used = new Set();
  for (const tab of [RESTAURANTE_TAB, INFANTIL_TAB]) {
    for (const sec of tab.sections) for (const it of sec.items) {
      for (const k of it.a) used.add(k);
      for (const entry of it.allergenRecord?.conditional || []) used.add(entry.key);
    }
  }
  return [...used].filter((k) => {
    const entry = allergenMap[k];
    return !entry || !entry.es || !entry.en || !entry.va;
  });
}
